/**
 * VÉRAULT Portal — Advisor: Concierge.
 *
 * The unified concierge experience: client↔advisor messaging, internal notes,
 * AI summaries, smart next actions, packing & destination intel, reminders.
 *
 * Layout (desktop):
 *   ┌─ Threads (left rail) ──┬─ Thread view (center) ──────┬─ Insights (right) ─┐
 *   │ filters · search       │ context header              │ AI summary         │
 *   │ list                   │ messages                    │ next actions       │
 *   │ + new                  │ composer                    │ packing · guidance │
 *   │                        │                             │ reminders          │
 *   └────────────────────────┴─────────────────────────────┴────────────────────┘
 *
 * Polling: thread list refetches every 30s; open thread refetches every 10s.
 * No suggested-reply chips (user explicitly deferred predictive autocomplete).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  Search, Plus, Send, Paperclip, Lock, Archive, ArchiveRestore,
  Sparkles, ListChecks, Backpack, Compass, Bell, BellOff, Loader2,
  MessageSquare, RefreshCw, ChevronRight, X, Calendar, User, Plane,
  Filter, Trash2, Eye, EyeOff, Mail, MailOpen,
} from "lucide-react";
import {
  Surface, PageHeader, EditorialRule, StatusTag, Empty, Skeleton,
  PortalButton, Heading, api, motion as portalMotion,
} from "../../index";
import { AttachmentPicker } from "../../components/AttachmentPicker";
import { AttachmentGrid, relTime, dayLabel } from "../../components/conciergeShared";
import { useAuth } from "../../../contexts/AuthContext";
import { TodaysDesk } from "./DeskSurfaces";
import InboundRepliesPanel from "./InboundRepliesPanel";
import {
  useTypingHeartbeat,
  useChatNotifications,
  primeAudio,
  useNotificationPrefs,
  toggleConversationMute,
} from "../../components/conciergeRealtime";

const { fadeInUp, stagger } = portalMotion;

// Phase 1.1 · Concierge Desk cleanup — separate client-level (no trip)
// from experience-level (linked to a trip) threads as first-class filters.
const FILTERS = [
  { id: "active", label: "Active" },
  { id: "unread", label: "Unread" },
  { id: "client", label: "Client threads" },
  { id: "experience", label: "Experience threads" },
  { id: "archived", label: "Archived" },
];

function fireChanged() {
  window.dispatchEvent(new CustomEvent("verault:concierge-changed"));
}

// ─── Thread list rail ───────────────────────────────────────────────
function ThreadList({ conversations, activeId, onSelect, filter, setFilter, search, setSearch, onNew, loading, onDeleted, onWipeAll, isOwner }) {
  return (
    <Surface variant="default" padded={false} className="flex flex-col h-full overflow-hidden">
      <div className="px-4 py-3 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between gap-2">
        <h3 className="font-serif text-base text-[hsl(var(--foreground))]">Conversations</h3>
        <div className="flex items-center gap-1">
          {isOwner && (
            <button
              onClick={onWipeAll}
              className="size-7 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--accent-terracotta))]/10 hover:text-[hsl(var(--accent-terracotta))] transition-colors"
              title="Owner-only — delete EVERY thread"
              data-testid="concierge-wipe-all"
            >
              <Trash2 className="size-3.5" strokeWidth={1.5} />
            </button>
          )}
          <button
            onClick={onNew}
            className="size-7 inline-flex items-center justify-center rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--accent-gold))] hover:text-[#1A1A1A] transition-colors"
            title="New conversation"
            data-testid="concierge-new-thread"
          >
            <Plus className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="px-4 py-2.5 border-b border-[rgba(0,0,0,0.06)] space-y-2">
        <div className="relative">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client, trip, or keyword"
            className="w-full pl-7 pr-2 py-1.5 text-xs bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.1)] placeholder:text-[hsl(var(--foreground-muted))]"
            data-testid="concierge-search"
          />
        </div>
        <div className="flex gap-1" data-testid="concierge-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-2 py-1 text-[11px] rounded transition-colors ${filter === f.id ? "bg-[#1A1A1A] text-white" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"}`}
              data-testid={`concierge-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">No conversations</p>
            <p className="mt-2 font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))]">
              Tap + to begin a thread with a client.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
            {conversations.map((c) => (
              <li key={c.id} className="relative group">
                <button
                  onClick={() => onSelect(c)}
                  className={`w-full text-left px-4 py-3 hover:bg-[hsl(var(--background-paper))]/50 transition-colors ${activeId === c.id ? "bg-[hsl(var(--background-paper))]" : ""}`}
                  data-testid={`concierge-thread-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-serif text-[14px] text-[hsl(var(--foreground))] truncate">{c.client_name || "Unnamed"}</span>
                        {c.unread_for_advisor > 0 && (
                          <span className="size-1.5 rounded-full bg-[hsl(var(--accent-gold))] shrink-0" aria-label="unread" />
                        )}
                      </div>
                      {c.trip_name && (
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--foreground-muted))] truncate">
                          {c.trip_name}
                        </p>
                      )}
                      <p className="mt-1 text-[12px] text-[hsl(var(--foreground-secondary))] truncate">
                        {c.last_sender_role === "client" && <span className="text-[hsl(var(--accent-gold))]">↳ </span>}
                        {c.last_message_preview || <span className="italic opacity-60">No messages yet</span>}
                      </p>
                    </div>
                    <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))] shrink-0">
                      {relTime(c.last_message_at)}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    if (!window.confirm(`Delete this thread with ${c.client_name || 'this client'}? This cannot be undone.`)) return;
                    try {
                      await api.delete(`/concierge/conversations/${c.id}`);
                      // Let the parent know to refresh.
                      if (typeof onDeleted === "function") onDeleted(c.id);
                    } catch (err) {
                      alert("Couldn't delete: " + (err?.response?.data?.detail || err?.message || "unknown error"));
                    }
                  }}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center size-7 rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--accent-terracotta))]/10"
                  title="Delete this thread"
                  data-testid={`concierge-thread-delete-${c.id}`}
                  aria-label="Delete thread"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.5} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Surface>
  );
}

// ─── Message bubble ─────────────────────────────────────────────────
function MessageRow({ msg, prevMsg, conversationId, onChanged }) {
  const isAdvisor = msg.sender_role === "advisor";
  const isInternal = msg.is_internal_note;
  // Wave 12.h — Edit / delete on the standalone advisor Concierge
  // page. Mirrors the Client Lounge composite — tap an advisor bubble
  // to open the action menu.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(msg.body || "");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    const t = setTimeout(() => document.addEventListener("click", close, { once: true }), 50);
    return () => { clearTimeout(t); document.removeEventListener("click", close); };
  }, [menuOpen]);

  const saveEdit = async () => {
    const body = (draft || "").trim();
    if (!body || !conversationId) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/concierge/conversations/${conversationId}/messages/${msg.id}`, { body });
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't save edit");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!conversationId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/concierge/conversations/${conversationId}/messages/${msg.id}`);
      await onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't delete");
    } finally { setBusy(false); }
  };

  // Feb-2026 · Per-message "Mark unread from here" for the advisor.
  // Never affects the client's read state (server-side isolation).
  const markUnreadFromHere = async () => {
    if (!conversationId) return;
    setBusy(true);
    try {
      await api.post(`/concierge/conversations/${conversationId}/messages/${msg.id}/mark-unread`);
      await onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't mark unread");
    } finally { setBusy(false); setMenuOpen(false); }
  };

  const editable = isAdvisor && conversationId; // only own messages
  // Every message row has a tap-menu (Mark unread available on all).
  // Edit / Delete are conditionally rendered for own messages only.
  const menuAvailable = !!conversationId && !isInternal;

  return (
    <div className={`flex ${isAdvisor ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.id}`}>
      <div className={`max-w-[78%] ${isInternal ? "w-full max-w-full" : ""}`}>
        {!isInternal && (
          <div className={`flex items-center gap-2 mb-1 ${isAdvisor ? "justify-end" : ""}`}>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--foreground-muted))]">
              {isAdvisor ? msg.sender_name || "Advisor" : msg.sender_name || "Client"}
            </span>
            <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">{relTime(msg.created_at)}</span>
          </div>
        )}

        {isInternal ? (
          <div className="rounded-lg border border-dashed border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/8 px-4 py-3" data-testid="internal-note">
            <div className="flex items-center justify-between mb-1.5">
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
                <Lock className="size-3" strokeWidth={1.5} /> Internal note · {msg.sender_name || "Advisor"}
              </span>
              <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">{relTime(msg.created_at)}</span>
            </div>
            <p className="text-[13px] text-[hsl(var(--foreground))] whitespace-pre-wrap leading-relaxed">{msg.body}</p>
            <AttachmentGrid attachments={msg.attachments} />
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (!editing && menuAvailable) setMenuOpen((o) => !o); }}
              className={`block w-full text-left rounded-2xl px-4 py-2.5 transition-all ${
                isAdvisor
                  ? "bg-[#1A1A1A] text-white rounded-tr-md hover:bg-black active:scale-[0.98]"
                  : "bg-white border border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground))] rounded-tl-md hover:border-[rgba(0,0,0,0.16)] active:scale-[0.99]"
              }`}
              data-testid={`message-${msg.id}-bubble`}
              aria-label={menuAvailable ? "Message · tap for options" : "Message"}
              disabled={editing || !menuAvailable}
            >
              {editing ? (
                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    onClick={(e) => e.stopPropagation()}
                    className={`w-full px-2 py-1.5 text-[13px] rounded resize-y outline-none ${
                      isAdvisor ? "bg-white/15 text-white placeholder-white/50" : "bg-white border border-[rgba(0,0,0,0.10)]"
                    }`}
                    data-testid={`message-${msg.id}-edit-textarea`}
                    autoFocus
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditing(false); setDraft(msg.body || ""); }}
                      disabled={busy}
                      className={`text-[10.5px] font-mono uppercase tracking-[0.14em] ${isAdvisor ? "text-white/70 hover:text-white" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                      disabled={busy || !draft.trim()}
                      data-testid={`message-${msg.id}-edit-save`}
                      className={`text-[10.5px] font-mono uppercase tracking-[0.14em] px-2.5 py-1 rounded ${isAdvisor ? "bg-white/20 text-white" : "bg-[hsl(var(--accent-gold))]/20 text-[hsl(var(--foreground))]"} disabled:opacity-50`}
                    >
                      {busy ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[14px] whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                  {msg.edited_at && (
                    <span className={`font-mono text-[9px] uppercase tracking-[0.14em] mt-1 inline-block ${isAdvisor ? "text-white/55" : "text-[hsl(var(--foreground-muted))]"}`}>
                      · edited
                    </span>
                  )}
                  <AttachmentGrid attachments={msg.attachments} />
                </>
              )}
            </button>
            {/* Tap action menu — appears below the bubble when open */}
            {menuOpen && !editing && menuAvailable && (
              <div
                className={`absolute ${isAdvisor ? "right-0" : "left-0"} top-full mt-1 z-20 flex flex-col rounded-md border border-[rgba(0,0,0,0.08)] bg-white shadow-lg min-w-[180px] overflow-hidden`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`message-${msg.id}-menu`}
              >
                <button
                  type="button"
                  onClick={() => markUnreadFromHere()}
                  disabled={busy}
                  className="text-left px-3 py-2 text-[13px] hover:bg-[hsl(var(--background-stone))]/50 flex items-center justify-between gap-2"
                  data-testid={`message-${msg.id}-mark-unread`}
                >
                  Mark unread from here
                  <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">Just me</span>
                </button>
                {editable && (
                  <>
                    <div className="h-px bg-[rgba(0,0,0,0.06)]" />
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setEditing(true); }}
                      className="text-left px-3 py-2 text-[13px] hover:bg-[hsl(var(--background-stone))]/50"
                      data-testid={`message-${msg.id}-edit`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); remove(); }}
                      className="text-left px-3 py-2 text-[13px] text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-stone))]/50"
                      data-testid={`message-${msg.id}-delete`}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Date divider ───────────────────────────────────────────────────
// ─── Wave 11q · Per-conversation mute toggle ───────────────────────
function ConversationMuteButton({ conversationId }) {
  const prefs = useNotificationPrefs();
  if (!conversationId) return null;
  const muted = (prefs?.muted_conversations || []).includes(conversationId);
  return (
    <button
      type="button"
      onClick={() => toggleConversationMute(conversationId, !muted)}
      className={`px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1.5 transition-colors ${
        muted
          ? "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))]"
          : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
      }`}
      title={muted ? "Unmute this thread" : "Mute this thread"}
      data-testid="conversation-mute-toggle"
      data-muted={muted ? "true" : "false"}
    >
      {muted ? <BellOff className="size-3" strokeWidth={1.5} /> : <Bell className="size-3" strokeWidth={1.5} />}
      <span className="font-mono text-[9.5px] uppercase tracking-[0.16em]">
        {muted ? "Muted" : "Mute"}
      </span>
    </button>
  );
}


function DateDivider({ label }) {
  return (
    <div className="flex items-center gap-3 my-4" aria-hidden>
      <div className="flex-1 h-px bg-[rgba(0,0,0,0.06)]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{label}</span>
      <div className="flex-1 h-px bg-[rgba(0,0,0,0.06)]" />
    </div>
  );
}

// ─── Typing indicator (Wave 11k) ───────────────────────────────────
function TypingDots({ label = "Typing" }) {
  return (
    <div className="flex items-end gap-2 mt-2" data-testid="typing-indicator">
      <div className="bg-white border border-[rgba(0,0,0,0.08)] rounded-2xl rounded-tl-md px-3.5 py-2 inline-flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="size-1.5 rounded-full bg-[hsl(var(--foreground-muted))]"
            style={{
              animation: `vt-typing-bounce 1.2s ease-in-out ${i * 0.18}s infinite`,
            }}
          />
        ))}
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.20em] text-[hsl(var(--foreground-muted))]">
        {label}…
      </span>
      <style>{`
        @keyframes vt-typing-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-3px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Composer ───────────────────────────────────────────────────────
function Composer({ onSend, busy, onTyping }) {
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const taRef = useRef(null);

  const handleSend = async () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    await onSend({ body, is_internal_note: internal, attachments });
    setText("");
    setAttachments([]);
    taRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`border-t ${internal ? "border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/5" : "border-[rgba(0,0,0,0.06)]"} px-5 py-3`} data-testid="composer">
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-[rgba(0,0,0,0.08)] text-[12px]">
              <span className="truncate max-w-[180px]">{a.name || a.url}</span>
              <button
                onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))]"
                type="button"
                data-testid={`composer-att-remove-${i}`}
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          // Don't ping for internal notes — those are advisor-private.
          if (e.target.value && !internal) onTyping?.();
        }}
        onFocus={() => primeAudio()}
        onKeyDown={handleKey}
        rows={2}
        placeholder={internal ? "Internal note (only you can see this)…" : "Write a message…"}
        className="w-full resize-none bg-transparent text-[14px] outline-none placeholder:text-[hsl(var(--foreground-muted))]"
        data-testid="composer-textarea"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPickerOpen(true)}
            className="size-8 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))] transition-colors"
            title="Attach"
            type="button"
            data-testid="composer-attach"
          >
            <Paperclip className="size-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => setInternal((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] transition-colors ${internal ? "bg-[hsl(var(--accent-gold))] text-[#1A1A1A]" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"}`}
            type="button"
            data-testid="composer-internal-toggle"
          >
            <Lock className="size-3" strokeWidth={1.5} /> Internal note
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))] hidden sm:inline">⌘↩ to send</span>
          <PortalButton
            variant={internal ? "secondary" : "primary"}
            size="sm"
            onClick={handleSend}
            disabled={busy || (!text.trim() && attachments.length === 0)}
            icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
            data-testid="composer-send"
          >
            {internal ? "Save note" : "Send"}
          </PortalButton>
        </div>
      </div>

      <AttachmentPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(att) => setAttachments((arr) => [...arr, att])}
      />
    </div>
  );
}

// ─── Right pane: AI surfaces & reminders ───────────────────────────
function InsightCard({ icon: Icon, title, hint, action, children, testid }) {
  return (
    <Surface variant="default" padded className="!p-5" data-testid={testid}>
      <div className="flex items-start gap-2.5 mb-2">
        <span className="inline-flex items-center justify-center size-7 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] shrink-0">
          <Icon className="size-3.5" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-[15px] text-[hsl(var(--foreground))] leading-tight">{title}</h4>
          {hint && <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--foreground-muted))]">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </Surface>
  );
}

function MarkdownPreview({ text }) {
  // Very light markdown rendering — headings, lists, bold. Keeps prose readable.
  if (!text) return null;
  const html = text
    .replace(/^## (.+)$/gm, '<h3 class="font-serif text-[14px] text-[hsl(var(--foreground))] mt-3 mb-1">$1</h3>')
    .replace(/^# (.+)$/gm, '<h3 class="font-serif text-[14px] text-[hsl(var(--foreground))] mt-3 mb-1">$1</h3>')
    .replace(/^- (.+)$/gm, '<div class="text-[13px] text-[hsl(var(--foreground-secondary))] pl-3 relative before:content-[\'·\'] before:absolute before:left-0 before:text-[hsl(var(--accent-gold))] mb-0.5">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^([^<\n][^\n]*)$/gm, '<p class="text-[13px] text-[hsl(var(--foreground-secondary))] leading-relaxed mb-1.5">$1</p>');
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function InsightsPane({ conv, refreshTrigger }) {
  const [summary, setSummary] = useState("");
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [actions, setActions] = useState(null);
  const [actionsBusy, setActionsBusy] = useState(false);
  const [packing, setPacking] = useState("");
  const [packingBusy, setPackingBusy] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [guidanceBusy, setGuidanceBusy] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [newReminderTitle, setNewReminderTitle] = useState("");

  // Reset AI surfaces when conversation changes
  useEffect(() => {
    setSummary(""); setActions(null); setPacking(""); setGuidance("");
  }, [conv?.id]);

  // Load reminders for the active conversation
  useEffect(() => {
    if (!conv?.id) return;
    api.get(`/concierge/reminders?conversation_id=${conv.id}`)
      .then(({ data }) => setReminders(data || [])).catch(() => setReminders([]));
  }, [conv?.id, refreshTrigger]);

  if (!conv) return null;

  const handleSummary = async () => {
    setSummaryBusy(true);
    try {
      const { data } = await api.post(`/concierge/conversations/${conv.id}/summarize`);
      setSummary(data.summary || "");
    } catch {
      setSummary("Summary unavailable right now.");
    } finally { setSummaryBusy(false); }
  };

  const handleActions = async () => {
    setActionsBusy(true);
    try {
      const { data } = await api.post(`/concierge/conversations/${conv.id}/next-actions`);
      setActions(data.actions || []);
    } catch {
      setActions([]);
    } finally { setActionsBusy(false); }
  };

  const handlePacking = async () => {
    if (!conv.trip_id) return;
    setPackingBusy(true);
    try {
      const { data } = await api.post("/concierge/packing-list", { trip_id: conv.trip_id });
      setPacking(data.markdown || "");
    } catch {
      setPacking("Packing list unavailable.");
    } finally { setPackingBusy(false); }
  };

  const handleGuidance = async () => {
    if (!conv.trip_id) return;
    setGuidanceBusy(true);
    try {
      const { data } = await api.post("/concierge/travel-guidance", { trip_id: conv.trip_id });
      setGuidance(data.markdown || "");
    } catch {
      setGuidance("Travel guidance unavailable.");
    } finally { setGuidanceBusy(false); }
  };

  const addReminder = async () => {
    const t = newReminderTitle.trim();
    if (!t) return;
    const { data } = await api.post("/concierge/reminders", {
      title: t,
      conversation_id: conv.id,
      client_id: conv.client_id,
      trip_id: conv.trip_id,
    });
    setReminders((arr) => [...arr, data]);
    setNewReminderTitle("");
  };

  const toggleReminder = async (r) => {
    const nextStatus = r.status === "done" ? "open" : "done";
    const { data } = await api.patch(`/concierge/reminders/${r.id}`, { status: nextStatus });
    setReminders((arr) => arr.map((x) => x.id === r.id ? data : x));
  };

  const deleteReminder = async (r) => {
    await api.delete(`/concierge/reminders/${r.id}`);
    setReminders((arr) => arr.filter((x) => x.id !== r.id));
  };

  return (
    <div className="space-y-4">
      <InsightCard
        icon={Sparkles}
        title="Thread summary"
        hint="A senior advisor's at-a-glance read"
        action={
          <button
            onClick={handleSummary}
            disabled={summaryBusy}
            className="text-[11px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] inline-flex items-center gap-1"
            data-testid="insight-summary-btn"
          >
            {summaryBusy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
            {summary ? "Refresh" : "Generate"}
          </button>
        }
        testid="insight-summary"
      >
        {summary ? (
          <p className="text-[13px] text-[hsl(var(--foreground-secondary))] leading-relaxed whitespace-pre-wrap">{summary}</p>
        ) : !summaryBusy && (
          <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
            Tap generate to read the thread in a few lines.
          </p>
        )}
      </InsightCard>

      <InsightCard
        icon={ListChecks}
        title="Smart next actions"
        hint="Operational moves worth making"
        action={
          <button
            onClick={handleActions}
            disabled={actionsBusy}
            className="text-[11px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] inline-flex items-center gap-1"
            data-testid="insight-actions-btn"
          >
            {actionsBusy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
            {actions ? "Refresh" : "Suggest"}
          </button>
        }
        testid="insight-actions"
      >
        {actions === null ? (
          !actionsBusy && (
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              Quiet suggestions, not autocomplete.
            </p>
          )
        ) : actions.length === 0 ? (
          <p className="text-[12px] text-[hsl(var(--foreground-muted))]">Nothing pressing.</p>
        ) : (
          <ul className="space-y-1.5">
            {actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-[hsl(var(--foreground-secondary))]">
                <span className="inline-flex size-4 shrink-0 mt-0.5 items-center justify-center rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] text-[10px] font-mono">{i + 1}</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}
      </InsightCard>

      {conv.trip_id && (
        <>
          <InsightCard
            icon={Backpack}
            title="Packing list"
            hint="Trip-aware checklist"
            action={
              <button
                onClick={handlePacking}
                disabled={packingBusy}
                className="text-[11px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] inline-flex items-center gap-1"
                data-testid="insight-packing-btn"
              >
                {packingBusy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                {packing ? "Refresh" : "Draft"}
              </button>
            }
            testid="insight-packing"
          >
            {packing ? (
              <div className="max-h-56 overflow-y-auto pr-1"><MarkdownPreview text={packing} /></div>
            ) : !packingBusy && (
              <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                Tap draft to compose a packing list based on this trip.
              </p>
            )}
          </InsightCard>

          <InsightCard
            icon={Compass}
            title="Destination intel"
            hint="Pace, customs, climate window"
            action={
              <button
                onClick={handleGuidance}
                disabled={guidanceBusy}
                className="text-[11px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] inline-flex items-center gap-1"
                data-testid="insight-guidance-btn"
              >
                {guidanceBusy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
                {guidance ? "Refresh" : "Draft"}
              </button>
            }
            testid="insight-guidance"
          >
            {guidance ? (
              <div className="max-h-56 overflow-y-auto pr-1"><MarkdownPreview text={guidance} /></div>
            ) : !guidanceBusy && (
              <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                Tap draft to compose a quiet destination brief.
              </p>
            )}
          </InsightCard>
        </>
      )}

      <InsightCard icon={Bell} title="Reminders" hint="Personal · advisor-only" testid="insight-reminders">
        <div className="space-y-1.5 mb-3">
          {reminders.length === 0 ? (
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              No reminders for this thread.
            </p>
          ) : reminders.map((r) => (
            <div key={r.id} className="group flex items-start gap-2 text-[13px]" data-testid={`reminder-${r.id}`}>
              <button
                onClick={() => toggleReminder(r)}
                className={`mt-0.5 size-4 shrink-0 rounded-full border transition-colors ${r.status === "done" ? "bg-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]" : "border-[rgba(0,0,0,0.2)] hover:border-[hsl(var(--accent-gold))]"}`}
                aria-label="toggle"
              />
              <span className={`flex-1 ${r.status === "done" ? "line-through text-[hsl(var(--foreground-muted))]" : "text-[hsl(var(--foreground-secondary))]"}`}>
                {r.title}
              </span>
              <button
                onClick={() => deleteReminder(r)}
                className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="delete"
              >
                <Trash2 className="size-3" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          <input
            value={newReminderTitle}
            onChange={(e) => setNewReminderTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addReminder(); }}
            placeholder="New reminder…"
            className="flex-1 px-2.5 py-1.5 text-xs bg-[hsl(var(--background-paper))] border border-transparent rounded outline-none focus:border-[rgba(0,0,0,0.1)] placeholder:text-[hsl(var(--foreground-muted))]"
            data-testid="reminder-input"
          />
          <button
            onClick={addReminder}
            disabled={!newReminderTitle.trim()}
            className="px-2.5 py-1.5 text-xs bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-30"
            type="button"
            data-testid="reminder-add"
          >
            Add
          </button>
        </div>
      </InsightCard>
    </div>
  );
}

// ─── New conversation modal ─────────────────────────────────────────
function NewThreadModal({ open, onClose, onCreated }) {
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedClient(null); setTripId(""); setTrips([]);
    api.get("/travels/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, [open]);

  useEffect(() => {
    if (!selectedClient) { setTrips([]); return; }
    api.get(`/travels/clients/${selectedClient.id}/trips`).then(({ data }) => setTrips(data || [])).catch(() => setTrips([]));
  }, [selectedClient]);

  const filtered = useMemo(() => {
    if (!clients) return null;
    const q = search.trim().toLowerCase();
    if (!q) return clients.slice(0, 20);
    return clients.filter((c) => (c.full_name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)).slice(0, 20);
  }, [clients, search]);

  const handleCreate = async () => {
    if (!selectedClient) return;
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/conversations", {
        client_id: selectedClient.id,
        trip_id: tripId || null,
      });
      onCreated(data);
      onClose();
    } catch {
      // Surface in console; modal stays open
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()} data-testid="new-thread-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E4DC]">
          <h3 className="font-serif text-lg text-[#1A1A1A]">Begin a conversation</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAFAF8] rounded" type="button">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {!selectedClient ? (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Client</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients…"
                autoFocus
                className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] mb-3"
                data-testid="new-thread-client-search"
              />
              {filtered === null ? (
                <p className="text-[12px] text-[#999]">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="text-[12px] text-[#999]">No clients match.</p>
              ) : (
                <ul className="max-h-64 overflow-y-auto divide-y divide-[#F0EDE6] border border-[#E8E4DC] rounded">
                  {filtered.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => setSelectedClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[#FAFAF8] transition-colors"
                        data-testid={`new-thread-client-${c.id}`}
                      >
                        <div className="text-sm text-[#1A1A1A]">{c.full_name || "Unnamed"}</div>
                        <div className="text-[11px] text-[#999]">{c.email}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div>
              <button onClick={() => setSelectedClient(null)} className="text-[11px] text-[#6B6B6B] hover:text-[#1A1A1A] mb-3" type="button">
                ← Change client
              </button>
              <div className="px-3 py-2 bg-[#FAFAF8] rounded mb-4">
                <div className="text-sm text-[#1A1A1A]">{selectedClient.full_name}</div>
                <div className="text-[11px] text-[#999]">{selectedClient.email}</div>
              </div>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Trip (optional)</label>
              <select
                value={tripId}
                onChange={(e) => setTripId(e.target.value)}
                className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] bg-white"
                data-testid="new-thread-trip-select"
              >
                <option value="">— General thread (no trip) —</option>
                {trips.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.client_trip_title || t.internal_trip_name || "Untitled trip"}
                  </option>
                ))}
              </select>
              <p className="mt-2 font-accent italic text-[12px] text-[#999]">
                One thread per (client + trip). If a thread already exists, it'll open that one.
              </p>
            </div>
          )}
        </div>
        {selectedClient && (
          <div className="px-6 py-4 border-t border-[#E8E4DC] flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" type="button">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={busy}
              className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50 inline-flex items-center gap-1.5"
              type="button"
              data-testid="new-thread-create"
            >
              {busy && <Loader2 className="size-3 animate-spin" />} Begin thread
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────
export default function AdvisorConcierge() {
  const { user } = useAuth();
  const isOwner = (user?.role || "").toLowerCase() === "owner";
  const [conversations, setConversations] = useState(null);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [showInternalOnly, setShowInternalOnly] = useState(false);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [sendingBusy, setSendingBusy] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesEndRef = useRef(null);

  // Honor ?conv=… deep-link from Journey/Client pages
  useEffect(() => {
    const qConv = searchParams.get("conv");
    if (qConv && qConv !== activeConvId) {
      setActiveConvId(qConv);
      setShowInternalOnly(false);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, activeConvId, setSearchParams]);

  // Fetch conversation list (polled)
  const fetchConversations = useCallback(async () => {
    const params = new URLSearchParams();
    // Phase 1.1 · Concierge cleanup
    // - active / unread / archived hit the backend status filter
    // - client / experience are pulled as 'active' and filtered client-side
    //   by trip_id presence (cheap; conversation list is tiny per advisor)
    if (filter === "active") params.set("status", "active");
    else if (filter === "archived") params.set("status", "archived");
    else if (filter === "unread") { params.set("status", "active"); params.set("has_unread", "true"); }
    else if (filter === "client" || filter === "experience") params.set("status", "active");
    if (search) params.set("q", search);
    try {
      const { data } = await api.get(`/concierge/conversations?${params.toString()}`);
      let rows = data || [];
      if (filter === "client") rows = rows.filter((c) => !c.trip_id);
      else if (filter === "experience") rows = rows.filter((c) => !!c.trip_id);
      setConversations(rows);
    } catch { setConversations([]); }
  }, [filter, search]);

  useEffect(() => {
    fetchConversations();
    const t = setInterval(fetchConversations, 30000);
    return () => clearInterval(t);
  }, [fetchConversations]);

  // Phase 1.1 · Concierge Desk cleanup
  // The default landing is a clean thread list — advisor explicitly picks
  // the thread to open. The previous auto-select-first behaviour caused
  // every visit to land mid-conversation with the most recent client.
  // Auto-select still happens only when a ?conv=… deep link is provided.

  // Fetch active conversation + messages (polled)
  const fetchActive = useCallback(async () => {
    if (!activeConvId) { setActiveConv(null); setMessages([]); return; }
    try {
      const [convRes, msgRes] = await Promise.all([
        api.get(`/concierge/conversations/${activeConvId}`),
        api.get(`/concierge/conversations/${activeConvId}/messages${showInternalOnly ? "?internal_only=true" : ""}`),
      ]);
      setActiveConv(convRes.data);
      setMessages(msgRes.data || []);
      // Auto mark-read on open — SKIPPED when the advisor has a manual
      // unread pointer set (they explicitly want this to stay unread).
      const hasManualPointer = !!convRes.data.advisor_unread_since_message_id;
      if ((convRes.data.unread_for_advisor || 0) > 0 && !hasManualPointer) {
        api.post(`/concierge/conversations/${activeConvId}/read-all`).then(() => {
          fireChanged();
          fetchConversations();
        }).catch(() => {});
      }
    } catch {
      setActiveConv(null); setMessages([]);
    }
  }, [activeConvId, showInternalOnly, fetchConversations]);

  useEffect(() => {
    fetchActive();
    if (!activeConvId) return;
    const t = setInterval(fetchActive, 4000);
    return () => clearInterval(t);
  }, [fetchActive, activeConvId]);

  // Wave 11k — typing heartbeat for outgoing composer
  const sendTypingPing = useTypingHeartbeat({
    role: "advisor",
    conversationId: activeConvId,
    disabled: !activeConvId,
  });

  // Wave 11k — new-message notification orchestration. Only fires when
  // the advisor tab is in the background.
  useChatNotifications({
    messages: messages.filter((m) => !m.is_internal_note),
    myRole: "advisor",
    senderLabel: activeConv?.client_name
      ? `${activeConv.client_name} · Verault`
      : "Verault Concierge",
    conversationId: activeConvId,
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, activeConvId, activeConv?.client_typing]);

  const handleSend = async (payload) => {
    if (!activeConvId) return;
    setSendingBusy(true);
    try {
      await api.post(`/concierge/conversations/${activeConvId}/messages`, payload);
      await fetchActive();
      fetchConversations();
      setRefreshTick((n) => n + 1);
    } finally { setSendingBusy(false); }
  };

  const archiveToggle = async () => {
    if (!activeConv) return;
    const next = activeConv.status === "archived" ? "active" : "archived";
    await api.patch(`/concierge/conversations/${activeConv.id}`, { status: next });
    await fetchConversations();
    await fetchActive();
  };

  // Group messages by day
  const grouped = useMemo(() => {
    const groups = [];
    let currentDay = "";
    for (const m of messages) {
      const d = dayLabel(m.created_at);
      if (d !== currentDay) {
        groups.push({ kind: "day", label: d, key: `day-${d}-${m.id}` });
        currentDay = d;
      }
      groups.push({ kind: "msg", msg: m, key: m.id });
    }
    return groups;
  }, [messages]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-6">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Concierge"
          title="The intelligent layer."
          lede="Quiet, threaded conversations with your clients — and a few editorial helpers when you want them."
        />
      </motion.div>

      {/* Phase 1.4 · Today's Desk — sits above the thread list as a calm "what needs me" feed. */}
      <motion.div variants={fadeInUp}>
        <TodaysDesk />
      </motion.div>

      {/* Feb 2026 · Phase 1 · Inbound Replies / Needs Attention.
          Lean triage list of new client/prospect emails detected via
          Nylas webhooks. Not a full inbox — a notification layer. */}
      <motion.div id="inbound-replies" variants={fadeInUp}>
        <InboundRepliesPanel />
      </motion.div>

      <motion.div variants={fadeInUp} className="grid grid-cols-12 gap-4 lg:gap-5" style={{ minHeight: "calc(100vh - 280px)" }}>
        {/* Thread list */}
        <div className="col-span-12 md:col-span-4 lg:col-span-3">
          <ThreadList
            conversations={conversations || []}
            activeId={activeConvId}
            onSelect={(c) => { setActiveConvId(c.id); setShowInternalOnly(false); }}
            filter={filter}
            setFilter={setFilter}
            search={search}
            setSearch={setSearch}
            onNew={() => setNewThreadOpen(true)}
            loading={conversations === null}
            onDeleted={(convId) => {
              setConversations((prev) => (prev || []).filter((x) => x.id !== convId));
              if (activeConvId === convId) {
                setActiveConvId(null);
                setActiveConv(null);
              }
            }}
            isOwner={isOwner}
            onWipeAll={async () => {
              const phrase = window.prompt(
                "This will DELETE EVERY conversation, message, and scheduled message in the database.\n\n" +
                "Type WIPE-ALL to confirm:"
              );
              if (phrase !== "WIPE-ALL") return;
              try {
                const { data } = await api.delete(`/concierge/conversations?confirm=WIPE-ALL`);
                window.alert(
                  `Done.\n\n` +
                  `Conversations deleted: ${data.conversations_deleted}\n` +
                  `Messages deleted: ${data.messages_deleted}\n` +
                  `Scheduled messages deleted: ${data.scheduled_messages_deleted}`
                );
                setConversations([]);
                setActiveConvId(null);
                setActiveConv(null);
              } catch (e) {
                window.alert("Couldn't wipe: " + (e?.response?.data?.detail || e?.message || "unknown"));
              }
            }}
          />
        </div>

        {/* Thread view */}
        <div className="col-span-12 md:col-span-8 lg:col-span-6">
          {!activeConv ? (
            <Surface variant="default" padded className="!p-12 h-full flex items-center justify-center">
              <Empty
                title="Begin a conversation"
                hint="Pick a thread on the left, or tap + to start a new one."
                icon={<MessageSquare className="size-6" strokeWidth={1.25} />}
              />
            </Surface>
          ) : (
            <Surface variant="default" padded={false} className="flex flex-col h-full overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3 border-b border-[rgba(0,0,0,0.06)] flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="font-serif text-lg text-[hsl(var(--foreground))] truncate" data-testid="thread-client">
                    {activeConv.client_name || "Client"}
                  </h2>
                  {activeConv.trip_name ? (
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                      <Plane className="inline size-3 mr-1" strokeWidth={1.5} /> {activeConv.trip_name}
                    </p>
                  ) : (
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                      General thread
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <ConversationMuteButton conversationId={activeConvId} />
                  {/* Feb-2026 · Thread-level mark unread / mark read.
                      Isolated per-participant on the server — this only
                      affects the advisor's read state, never the client's. */}
                  <button
                    onClick={async () => {
                      const isUnread = !!activeConv.advisor_unread_since_message_id;
                      try {
                        if (isUnread) {
                          await api.post(`/concierge/conversations/${activeConvId}/read-all`);
                        } else {
                          await api.post(`/concierge/conversations/${activeConvId}/mark-unread`);
                        }
                        fireChanged();
                        fetchConversations();
                        fetchActive();
                      } catch (e) {
                        // eslint-disable-next-line no-alert
                        alert(e?.response?.data?.detail || "Couldn't update read state");
                      }
                    }}
                    title={activeConv.advisor_unread_since_message_id
                      ? "Mark this conversation as read (just for you)"
                      : "Mark this conversation as unread (just for you)"}
                    className="size-8 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))] transition-colors"
                    type="button"
                    data-testid="thread-toggle-unread"
                    aria-label={activeConv.advisor_unread_since_message_id ? "Mark as read" : "Mark as unread"}
                  >
                    {activeConv.advisor_unread_since_message_id
                      ? <MailOpen className="size-4" strokeWidth={1.5} />
                      : <Mail className="size-4" strokeWidth={1.5} />}
                  </button>
                  <button
                    onClick={() => setShowInternalOnly((v) => !v)}
                    title={showInternalOnly ? "Show all messages" : "Show internal notes only"}
                    className={`px-2.5 py-1 rounded-full text-[11px] inline-flex items-center gap-1.5 transition-colors ${showInternalOnly ? "bg-[hsl(var(--accent-gold))] text-[#1A1A1A]" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"}`}
                    type="button"
                    data-testid="thread-internal-filter"
                  >
                    {showInternalOnly ? <Eye className="size-3" strokeWidth={1.5} /> : <EyeOff className="size-3" strokeWidth={1.5} />}
                    {showInternalOnly ? "Notes only" : "All"}
                  </button>
                  <button
                    onClick={archiveToggle}
                    title={activeConv.status === "archived" ? "Unarchive" : "Archive"}
                    className="size-8 inline-flex items-center justify-center rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
                    type="button"
                    data-testid="thread-archive"
                  >
                    {activeConv.status === "archived" ? <ArchiveRestore className="size-4" strokeWidth={1.5} /> : <Archive className="size-4" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="thread-messages">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
                      {showInternalOnly ? "No internal notes yet." : "No messages yet — say hello."}
                    </p>
                  </div>
                ) : (
                  grouped.map((g) =>
                    g.kind === "day" ? <DateDivider key={g.key} label={g.label} /> : <MessageRow key={g.key} msg={g.msg} conversationId={activeConvId} onChanged={fetchActive} />
                  )
                )}
                {activeConv?.client_typing ? (
                  <TypingDots label={`${(activeConv.client_name || "Client").split(" ")[0]} is typing`} />
                ) : null}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <Composer onSend={handleSend} busy={sendingBusy} onTyping={sendTypingPing} />
            </Surface>
          )}
        </div>

        {/* Insights pane */}
        <div className="col-span-12 lg:col-span-3 hidden lg:block">
          {activeConv && <InsightsPane conv={activeConv} refreshTrigger={refreshTick} />}
        </div>
      </motion.div>

      <NewThreadModal
        open={newThreadOpen}
        onClose={() => setNewThreadOpen(false)}
        onCreated={(conv) => { setActiveConvId(conv.id); fetchConversations(); }}
      />
    </motion.div>
  );
}
